import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'

export const PackagingInfoContext = createContext()

export function usePackagingInfoContext() {
  return useContext(PackagingInfoContext)
}

export function PackagingInfoContextProvider({ children }) {
  const [spreadsheetData, setSpreadsheetData] = useState({
    regular: [],
    swaps: [],
  })
  const [isLoading, setIsLoading] = useState(false)
  const hasLoadedRef = useRef(false)
  const isLoadingRef = useRef(false)

  const loadSpreadsheetData = useCallback(async () => {
    if (hasLoadedRef.current || isLoadingRef.current) return

    isLoadingRef.current = true
    setIsLoading(true)
    try {
      const response = await fetch('/api/packaging-info/spreadsheet-data', {
        headers: {
          'x-account-api-key': process.env.NEXT_PUBLIC_ACCOUNT_ENDPOINT_API_KEY || '',
        },
      })
      const result = await response.json()

      if (result.message === 'success' && result.data) {
        setSpreadsheetData({
          regular: result.data.regular || [],
          swaps: result.data.swaps || [],
        })
        hasLoadedRef.current = true
      }
    } catch (error) {
      console.error('Error loading packaging spreadsheet data:', error)
    } finally {
      isLoadingRef.current = false
      setIsLoading(false)
    }
  }, [])

  /**
   * Get packaging info for projected harvest by item SKU and future Box SKU
   * @param {string} itemSku - The item SKU from Sanity (e.g., "KING-SALMON-1LB")
   * @param {string} futureBoxSku - The future Box SKU (e.g., "SP-JAN")
   * @param {boolean} isSwap - Whether this is a swap item
   * @returns {Object|null} Packaging information object or null if not found
   */
  const getPackagingInfo = useCallback(
    (itemSku, futureBoxSku, isSwap = false) => {
      if (!itemSku || !futureBoxSku || !hasLoadedRef.current) {
        return null
      }

      const dataRows = isSwap
        ? spreadsheetData.swaps.slice(1) // Skip header row
        : spreadsheetData.regular.slice(1) // Skip header row

      if (!dataRows || dataRows.length === 0) {
        return null
      }

      let matchingRow = null

      if (isSwap) {
        // Swaps structure: Subscription SKU (0) | SWP SKU (1) | Replacement Product ID (2) | Replacement Variant ID (3) | Replacement Product SKU (4) | Replacement Product Name (5) | Replacement Quantity (6) | Packaging Type (7) | Weight Value (8) | Weight Unit (9)
        matchingRow = dataRows.find((row) => {
          const rowSubscriptionSku = row[0]?.trim()
          const rowReplacementProductSku = row[1]?.trim() // SWP SKU is at index 1
          return (
            rowSubscriptionSku === futureBoxSku &&
            rowReplacementProductSku === itemSku
          )
        })

        if (matchingRow) {
          // Swaps: Replacement Quantity (6) | Packaging Type (7) | Weight Value (8) | Weight Unit (9)
          const quantity = matchingRow[6]?.trim() || ''
          const packagingType = matchingRow[7]?.trim() || ''
          const weightValue = matchingRow[8]?.trim() || ''
          const weightUnit = matchingRow[9]?.trim() || ''

          return {
            quantity,
            packaging_type: packagingType,
            weight_value: weightValue,
            weight_unit: weightUnit,
            display: buildPackagingDisplayString(
              quantity,
              packagingType,
              weightValue,
              weightUnit,
            ),
          }
        }
      } else {
        // Regular structure: Box SKU (0) | Product ID (1) | Variant ID (2) | Item SKU (3) | Item Name (4) | Quantity (5) | Packaging Type (6) | Weight Value (7) | Weight Unit (8)
        matchingRow = dataRows.find((row) => {
          const rowBoxSku = row[0]?.trim()
          const rowItemSku = row[3]?.trim() // Item SKU is at index 3
          return (
            rowBoxSku === futureBoxSku && rowItemSku === itemSku
          )
        })

        if (matchingRow) {
          const quantity = matchingRow[5]?.trim() || ''
          const packagingType = matchingRow[6]?.trim() || ''
          const weightValue = matchingRow[7]?.trim() || ''
          const weightUnit = matchingRow[8]?.trim() || ''

          return {
            quantity,
            packaging_type: packagingType,
            weight_value: weightValue,
            weight_unit: weightUnit,
            display: buildPackagingDisplayString(
              quantity,
              packagingType,
              weightValue,
              weightUnit,
            ),
          }
        }
      }

      return null
    },
    [spreadsheetData],
  )

  // Load data on mount (only once)
  useEffect(() => {
    if (!hasLoadedRef.current && !isLoadingRef.current) {
      loadSpreadsheetData()
    }
  }, [loadSpreadsheetData])

  return (
    <PackagingInfoContext.Provider
      value={{
        spreadsheetData,
        isLoading,
        hasLoaded: hasLoadedRef.current,
        loadSpreadsheetData,
        getPackagingInfo,
      }}
    >
      {children}
    </PackagingInfoContext.Provider>
  )
}

// Helper function to build display string
function buildPackagingDisplayString(
  quantity,
  packagingType,
  weightValue,
  weightUnit,
) {
  const hasPackaging = quantity && packagingType
  const packagingPart = hasPackaging ? `${quantity} ${packagingType}` : ''

  const hasWeight = weightValue && weightUnit
  const weightPart = hasWeight ? `${weightValue} ${weightUnit}` : ''

  if (packagingPart && weightPart) {
    return `${packagingPart} of ${weightPart}`
  } else if (packagingPart) {
    return packagingPart
  } else if (weightPart) {
    return weightPart
  }

  return ''
}

